import styled, { keyframes, css } from "styled-components";
import { media } from "../../utils/breakpoints";

// In Progress row exit animation. When a vehicle's row leaves the In Progress
// list (it reached the picked station, departed two stops away, etc.) we don't
// want it to pop out of existence. Instead it gives a slight size bump/bounce
// then fades to zero while sliding in the direction the vehicle was travelling
// along the distance line: rightward for departing/continuing (FROM) legs whose
// marker runs left→right, leftward for approaching (TO) legs whose marker runs
// right→left. The brief scale(1.05) is the bump; the back-out easing on the
// animation supplies the bounce.
export const inProgressExitRight = keyframes`
  0%   { transform: translateX(0) scale(1);       opacity: 1; }
  28%  { transform: translateX(2px) scale(1.05);  opacity: 1; }
  55%  { transform: translateX(14px) scale(1);    opacity: 0.85; }
  100% { transform: translateX(60px) scale(0.97); opacity: 0; }
`;

export const inProgressExitLeft = keyframes`
  0%   { transform: translateX(0) scale(1);        opacity: 1; }
  28%  { transform: translateX(-2px) scale(1.05);  opacity: 1; }
  55%  { transform: translateX(-14px) scale(1);    opacity: 0.85; }
  100% { transform: translateX(-60px) scale(0.97); opacity: 0; }
`;

export const NewStationViewWrapperStyled = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const HeaderStyled = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  /* Stretch columns to the full header height so the middle column's
     Call Vehicle + Map View can flex-grow proportionally. */
  align-items: stretch;
  padding: 16px 24px;
  gap: 23px;
  width: 100%;
  /* Fluid: 140px on a tablet landscape (1366×768), grows toward the original
     280px on wide desktop monitors so labels stay readable. The tablet+portrait
     media queries below pin tighter values inside their breakpoints.
     min-height (not height) so that on a short viewport — where 18vh collapses
     to the 140px floor — the header still grows to contain the left column's
     zone block + station picker instead of letting them spill past its bottom
     edge into the tabs section below. */
  min-height: clamp(140px, 18vh, 280px);
  background: rgba(244, 244, 244, 0.5);
  border-bottom: 1px solid #f4f4f4;
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;
  z-index: 10;
  position: relative;

  ${media.tablet} {
    /* min-height (was a hard 160px) so a short tablet-width viewport grows the
       header to fit the left column instead of clipping/spilling it. */
    min-height: 160px;
    padding: 14px 16px;
    gap: 16px;
  }

  ${media.portrait} {
    height: auto;
    min-height: 0;
    padding: 16px;
    gap: 12px;
  }
`;

export const TopControlPanelStyled = styled.div`
  display: flex;
  flex-direction: row;
  gap: 23px;
  align-items: flex-start;
  flex: 1;
  min-width: 0;

  align-items: stretch;

  ${media.tablet} {
    gap: 16px;
  }

  ${media.portrait} {
    /* Grid lets us place flattened leaves: Left full-width, then Call Vehicle
       (2fr) beside No Vehicles (1fr), then Map View full-width below. The
       Middle wrappers use display:contents so the button/map become grid items
       directly (see MiddleColumn / ActionCardPlaceholder / InteractionPane). */
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 12px;
    width: 100%;
    grid-template-areas:
      "left left"
      "call novc"
      "map  map"
      "err  err";
  }
`;

export const LeftColumnStyled = styled.div`
  display: flex;
  flex-direction: column;
  /* Pin zone block to top and station picker to bottom; spread fills
     whatever vertical space the stretched header provides. Gap is the
     minimum visible breathing room; space-between distributes any extra. */
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  width: 678px;
  flex: 1 0 auto;

  ${media.tablet} {
    width: auto;
    flex: 1 1 0;
    min-width: 0;
  }

  ${media.portrait} {
    /* Stacked grid item — no header height to spread into, use a gap. */
    grid-area: left;
    gap: 8px;
    width: 100%;
    min-height: 0;
  }
`;

export const ZoneBlockStyled = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  flex: 0 0 auto;
`;

export const ZoneInfoStyled = styled.div`
  display: flex;
  /* Row layout: icon sits left of the zone label + name block */
  flex-direction: row;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  position: relative;

  svg {
    /* Fluid icon: 32px on tablet landscape, scales toward the original 40px on
       desktop. */
    width: clamp(32px, 2.2vw, 40px);
    height: clamp(32px, 2.2vw, 40px);
    flex-shrink: 0;
    /* Nudge icon down to align baseline with the label text */
    margin-top: 2px;
  }

  > div {
    position: relative;
    flex: 1;
    min-width: 0;
  }
`;

export const ZoneLabelStyled = styled.div`
  font-family: "Readex Pro", sans-serif;
  font-weight: 500;
  /* 18px on tablet landscape → grows toward the original 24px on desktop. */
  font-size: clamp(18px, 1.4vw, 24px);
  line-height: 120%;
  letter-spacing: -0.01em;
  color: #606060;
  display: flex;
  align-items: center;
  /* Track the font with the same 1.2 line-height ratio so the row doesn't clip. */
  min-height: calc(clamp(18px, 1.4vw, 24px) * 1.2);

  ${media.tablet} {
    font-size: 16px;
    min-height: 20px;
  }
`;

export const ZoneNameRowStyled = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  width: 100%;
  /* Tracks ZoneNameStyled font-size × 1.2 line-height fluidly. */
  min-height: calc(clamp(36px, 3vw, 48px) * 1.2);
  position: relative;
`;

export const ZoneNameStyled = styled.div`
  font-family: "Readex Pro", sans-serif;
  font-weight: 400;
  /* 36px on tablet landscape → grows toward the original 48px on desktop. */
  font-size: clamp(36px, 3vw, 48px);
  line-height: 120%;
  letter-spacing: -0.02em;
  color: #3f7f7d;
  display: flex;
  align-items: center;
  min-height: calc(clamp(36px, 3vw, 48px) * 1.2);
  cursor: pointer;

  ${media.tablet} {
    font-size: 28px;
    min-height: 34px;
  }

  ${media.portrait} {
    font-size: 24px;
    min-height: 0;
  }
`;

export const SelectorsBlockStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0px;
  width: 100%;
  flex: 0 0 auto;
`;

export const MiddleColumnStyled = styled.div`
  display: flex;
  flex-direction: column;
  /* Stretches to full header height; StationActions inside flexes
     Call Vehicle and Map View to fill it. */
  align-items: stretch;
  justify-content: stretch;
  gap: 6px;
  /* Track the Call Vehicle / Map View button growth so they don't overflow
     past the column at wide viewports. 400px tablet → 560px desktop. */
  width: clamp(400px, 32vw, 560px);
  flex-shrink: 0;

  ${media.tablet} {
    width: clamp(280px, 32vw, 400px);
  }

  ${media.portrait} {
    /* Flatten so the inner Call Vehicle / Map View become direct grid items
       of TopControlPanel. */
    display: contents;
  }
`;

export const RightColumnStyled = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 268px;
  flex-shrink: 0;

  ${media.tablet} {
    width: clamp(220px, 22vw, 268px);
  }

  ${media.portrait} {
    grid-area: novc;
    width: auto;
    min-width: 0;
    min-height: 0;
  }
`;

export const ActionCardPlaceholderStyled = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;

  ${media.portrait} {
    /* Flatten so the Call Vehicle button / Map View reach the parent grid. */
    display: contents;
  }
`;

// Tabs Section Styles
export const StatusTabsWrapperStyled = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  position: relative;
  z-index: 0;
`;

export const TabsSectionStyled = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-start;
  padding: 0px 24px;
  width: 100%;
  background: #ffffff;
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;
  z-index: 0;
`;

export const TabsContainerStyled = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px;
  /* Floor: 472px at tablet (16px label fits 236px tabs). Grows with viewport
     toward 720px so larger desktop labels (clamp 22px) don't truncate. */
  width: clamp(472px, 36vw, 720px);
  height: 80px;
  flex: none;
  order: 0;
  flex-grow: 0;

  ${media.tablet} {
    width: 100%;
    max-width: 472px;
  }
`;

export const TabStyled = styled.div<{ $isActive: boolean }>`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 18px 32px;
  gap: 16px;
  /* Share the (now fluid) container equally; floor matches the 236px desktop
     base so visual rhythm holds at the tablet breakpoint. */
  width: auto;
  flex: 1 1 0;
  min-width: 236px;
  height: 80px;
  background: #ffffff;
  border-bottom: ${(props) => (props.$isActive ? "2px solid #172A3F" : "none")};
  flex: none;
  order: 0;
  flex-grow: 0;
  cursor: pointer;
  transition: border-bottom 0.2s ease;

  ${media.tablet} {
    width: auto;
    flex: 1 1 0;
    padding: 14px 20px;
    gap: 12px;
    min-height: 60px;
  }
`;

export const TabIconStyled = styled.div`
  width: 40px;
  height: 40px;
  flex: none;
  order: 0;
  flex-grow: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 40px;
    height: 40px;
    display: block;
  }
`;

export const TabIconContainerStyled = styled.div<{
  $bgColor: string;
  $opacity?: number;
}>`
  width: 40px;
  height: 40px;
  position: relative;
  flex: none;
  order: 0;
  flex-grow: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: "";
    position: absolute;
    left: -7.5%;
    right: -7.5%;
    top: -7.5%;
    bottom: -7.5%;
    width: calc(100% + 15%);
    height: calc(100% + 15%);
    background: ${(props) => props.$bgColor};
    opacity: ${(props) => props.$opacity ?? 1};
    clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
    z-index: 0;
  }

  svg {
    position: relative;
    width: 24px;
    height: 24px;
    z-index: 1;

    path,
    circle {
      fill: inherit;
    }
  }
`;

export const TabContentStyled = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  flex: none;
  order: 1;
  flex-grow: 0;
`;

export const TabCountStyled = styled.div<{ $color: string }>`
  width: auto;
  /* Track font for clean baseline alignment. */
  height: auto;
  font-family: "Readex Pro", sans-serif;
  font-style: normal;
  font-weight: 500;
  /* 20px tablet → 28px desktop. */
  font-size: clamp(20px, 1.6vw, 28px);
  line-height: 120%;
  color: ${(props) => props.$color};
  flex: none;
  order: 0;
  flex-grow: 0;
`;

export const TabLabelStyled = styled.div<{
  $color: string;
  $fontWeight: number;
}>`
  width: auto;
  height: auto;
  font-family: "Readex Pro", sans-serif;
  font-style: normal;
  font-weight: ${(props) => props.$fontWeight};
  /* 16px tablet → 22px desktop. */
  font-size: clamp(16px, 1.2vw, 22px);
  line-height: 128%;
  color: ${(props) => props.$color};
  flex: none;
  order: 1;
  flex-grow: 0;
`;

export const DividerStyled = styled.div`
  width: 100%;
  height: 0px;
  border: 1px solid #d1d1d1;
  flex: none;
  order: 1;
  align-self: stretch;
  flex-grow: 0;
`;

// Tab Content Styles
export const TabContentContainerStyled = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px 0px 24px 0px;
  width: 100%;
  flex: 1;
  order: 1;
  align-self: stretch;
  flex-grow: 1;
  z-index: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  position: relative;
`;

export const ListItemStyled = styled.div<{
  $isLast?: boolean;
  // DEV-3706 (In Progress parity): grey the whole row when the vehicle is
  // offline. Only the In Progress tab passes this; Action Required rows leave
  // it unset and render unchanged.
  $offline?: boolean;
  // Exit animation (In Progress only): when the row is leaving the list it
  // plays the slide+fade keyframes above instead of unmounting abruptly.
  $exiting?: boolean;
  $exitDir?: "left" | "right";
}>`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 24px 24px ${(props) => (props.$isLast ? "24px" : "0px")};
  gap: 24px;
  isolation: isolate;
  width: 100%;
  min-height: 116px;
  background: #ffffff;
  flex: none;
  flex-grow: 0;
  opacity: ${(props) => (props.$offline ? 0.55 : 1)};

  ${media.tablet} {
    padding: 16px 16px ${(props) => (props.$isLast ? "16px" : "0px")};
    gap: 16px;
  }

  ${(props) =>
    props.$exiting &&
    css`
      pointer-events: none;
      will-change: transform, opacity;
      animation: ${props.$exitDir === "left"
          ? inProgressExitLeft
          : inProgressExitRight}
        560ms cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
    `}
`;

export const ListItemContentStyled = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 0px;
  gap: 24px;
  width: 100%;
  height: 68px;
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;
  z-index: 1;

  ${media.tablet} {
    gap: 16px;
  }

  ${media.portrait} {
    height: auto;
    min-height: 68px;
    align-items: stretch;
    gap: 12px;
  }

  ${media.portraitMobile} {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

export const ListItemMainContentStyled = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px;
  gap: 24px;
  flex: none;
  order: 0;
  flex-grow: 1;
  min-width: 0;

  ${media.tablet} {
    gap: 16px;
  }

  ${media.portrait} {
    display: grid;
    grid-template-columns: auto auto 1fr;
    grid-template-areas:
      "img    name    mission"
      "icon   badge   mission";
    column-gap: 12px;
    row-gap: 8px;
    align-items: center;
    padding: 8px 0;
  }

  /* Narrow portrait: single compact horizontal row, ordered
     status-icon | vehicle-image | name/battery | status-pill. */
  ${media.portraitMobile} {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    padding: 0;
  }
`;

// Vehicle Image Styles
export const VehicleImageContainerStyled = styled.div`
  box-sizing: border-box;
  position: relative;
  width: 104px;
  height: 68px;
  flex: none;
  order: 0;
  flex-grow: 0;
  z-index: 0;

  ${media.portrait} {
    grid-area: img;
  }

  ${media.portraitMobile} {
    order: 2;
    width: 72px;
    height: 48px;
    flex-shrink: 0;
  }
`;

export const VehicleImageBgStyled = styled.div`
  box-sizing: border-box;
  position: absolute;
  width: 104px;
  height: 68px;
  left: calc(50% - 104px / 2);
  top: calc(50% - 68px / 2);
  background: rgba(85, 184, 179, 0.1);
  border: 1px solid #55b8b3;
  flex: none;
  order: 0;
  flex-grow: 0;
  z-index: 0;

  ${media.portraitMobile} {
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
  }
`;

export const VehicleImageStyled = styled.img`
  position: absolute;
  left: calc(50% - 104px / 2);
  top: calc(50% - 68px / 2);
  width: 104px;
  height: 68px;
  object-fit: contain;
  z-index: 2;
  pointer-events: none;

  ${media.portraitMobile} {
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
  }
`;

// Info Section Styles
export const InfoSectionStyled = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px;
  gap: 40px;
  flex: none;
  order: 1;
  align-self: stretch;
  flex-grow: 1;
  min-width: 0;

  ${media.tablet} {
    gap: 20px;
  }

  ${media.portrait} {
    /* Flatten so descendants (icon, badge, mission name) can be placed
       directly into the parent grid via grid-area. */
    display: contents;
  }
`;

export const TitleBatteryStyled = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 0px;
  gap: 16px;
  width: 114px;
  /* min-height instead of a hard 68px — vehicle name (24px) + gap (16px) +
   * battery (32px) = 72px, so the original hard height clipped the bottom 4px
   * of the battery glyph. */
  min-height: 72px;
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;

  ${media.portrait} {
    grid-area: name;
  }

  ${media.portraitMobile} {
    order: 3;
    width: auto;
    min-width: 0;
    min-height: 0;
    gap: 4px;
  }
`;

export const VehicleNameStyled = styled.div`
  width: 140px;
  height: 24px;
  font-family: "Readex Pro", sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  color: #1b1b1b;
  flex: none;
  order: 0;
  flex-grow: 0;

  ${media.portraitMobile} {
    width: auto;
    max-width: 96px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const BatteryContainerStyled = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 0px;
  gap: 8px;
  width: 77px;
  height: 32px;
  flex: none;
  order: 1;
  flex-grow: 0;

  ${media.portraitMobile} {
    height: 24px;
  }
`;

export const BatteryFrameStyled = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px;
  width: 77px;
  height: 32px;
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 1;

  svg {
    width: 77px;
    height: 32px;
    display: block;
  }

  ${media.portraitMobile} {
    width: 58px;
    height: 24px;

    svg {
      width: 58px;
      height: 24px;
    }
  }
`;

export const VerticalSeparatorStyled = styled.div`
  width: 0px;
  height: 68px;
  border: 1px solid #bbbbbb;
  flex: none;
  order: 1;
  align-self: stretch;
  flex-grow: 0;

  ${media.portrait} {
    display: none;
  }
`;

// Mission Status Styles
export const MissionStatusStyled = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 0px;
  gap: 12px;
  flex: none;
  order: 2;
  flex-grow: 1;

  ${media.portrait} {
    display: contents;
  }
`;

export const MissionStatusIconContainerStyled = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 4px;
  gap: 8px;
  isolation: isolate;
  width: 32px;
  height: 32px;
  flex: none;
  order: 0;
  flex-grow: 0;
  position: relative;

  ${media.portrait} {
    grid-area: icon;
    justify-self: center;
  }

  ${media.portraitMobile} {
    order: 1;
    width: 48px;
    height: 48px;
    align-items: center;
    justify-content: center;
  }
`;

export const MissionStatusIconBgStyled = styled.div<{ $bgColor: string }>`
  position: absolute;
  width: 32px;
  height: 32px;
  left: 0px;
  top: 0px;
  flex: none;
  order: 0;
  flex-grow: 0;
  z-index: 0;
  border-radius: 4px;
  background: ${(props) => props.$bgColor};

  ${media.portraitMobile} {
    width: 48px;
    height: 48px;
  }
`;

export const MissionStatusIconStyled = styled.div`
  width: 24px;
  height: 24px;
  flex: none;
  order: 1;
  flex-grow: 0;
  z-index: 1;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 24px;
    height: 24px;
  }

  ${media.portraitMobile} {
    width: 40px;
    height: 40px;

    svg {
      width: 32px;
      height: 32px;
    }
  }
`;

export const MissionStatusTextStyled = styled.div`
  width: auto;
  height: 24px;
  font-family: "Readex Pro", sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 20px;
  line-height: 120%;
  color: #1b1b1b;
  flex: none;
  order: 1;
  flex-grow: 0;
`;

// Button Styles
export const ActionButtonStyled = styled.button<{
  $variant?: "primary" | "secondary";
  $small?: boolean;
  // DEV-3706: greyed/non-interactive when the vehicle is offline.
  $disabled?: boolean;
}>`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 4px;
  height: 68px;
  min-height: 44px;
  flex: none;
  order: 1;
  flex-grow: 0;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  font-family: "Readex Pro", sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 20px;
  line-height: 120%;
  border: none;
  transition: opacity 0.2s ease;

  ${(props) =>
    props.$small
      ? "width: 56px; padding: 22px 16px;"
      : "width: 192px; padding: 22px 16px;"}

  ${(props) =>
    props.$variant === "primary"
      ? `
    border: 2px solid #3F7F7D;
    background: #FFFFFF;
    color: #3F7F7D;
  `
      : `
    background: #F0E0D1;
    color: #864C13;
  `}

  ${media.tablet} {
    font-size: 18px;
    ${(props) =>
      props.$small
        ? "width: 52px; padding: 16px 12px;"
        : "width: 160px; padding: 16px 12px;"}
  }

  ${media.portrait} {
    height: 100%;
    min-height: 96px;
    align-self: stretch;
    ${(props) =>
      props.$small
        ? "width: 72px; padding: 16px 12px;"
        : "width: 220px; padding: 16px 12px; font-size: 22px;"}
  }

  ${media.portraitMobile} {
    /* Row is a column now; button sits full-width below the content. */
    height: auto;
    min-height: 72px;
    ${(props) =>
      props.$small
        ? "width: 72px; padding: 16px 12px;"
        : "width: auto; padding: 16px 12px; font-size: 20px;"}
  }

  &:hover {
    opacity: 0.8;
  }

  ${(props) =>
    props.$disabled
      ? `
    background: #E0E0E0;
    color: #9A9A9A;
    pointer-events: none;
    &:hover { opacity: 1; }
  `
      : ""}
`;

export const ButtonGroupStyled = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex: none;
  order: 1;
  flex-grow: 0;

  ${media.portrait} {
    align-self: stretch;
    align-items: stretch;
  }

  ${media.portraitMobile} {
    /* Full-width button row below the content; release variant fills the
       space next to the small re-dispatch icon button. */
    width: 100%;

    > button[data-button-type="release"] {
      flex: 1;
    }
  }
`;

export const ButtonTextStyled = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 0px 8px;
  gap: 8px;
  flex: none;
  order: 1;
  flex-grow: 0;
`;

export const ButtonLabelStyled = styled.span`
  width: auto;
  height: 24px;
  font-family: "Readex Pro", sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 20px;
  line-height: 120%;
  flex: none;
  order: 0;
  flex-grow: 0;
`;

export const ButtonIconStyled = styled.div`
  width: 24px;
  height: 24px;
  flex: none;
  order: 2;
  flex-grow: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 24px;
    height: 24px;
  }
`;

export const ListItemDividerStyled = styled.div`
  width: 100%;
  height: 0px;
  border: 1px solid #d1d1d1;
  flex: none;
  order: 1;
  align-self: stretch;
  flex-grow: 0;
  z-index: 0;
`;

// In Progress Styles
export const InProgressListItemContentStyled = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px;
  gap: 24px;
  width: 100%;
  height: 68px;
  flex: 1 1 auto;
  order: 0;
  align-self: stretch;
  min-width: 0;

  ${media.tablet} {
    gap: 16px;
  }

  ${media.portrait} {
    height: auto;
    min-height: 68px;
    align-items: center;
    gap: 12px;
  }
`;

export const InProgressInfoSectionStyled = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px;
  gap: 24px;
  flex: 1 1 auto;
  order: 0;
  min-width: 0;

  ${media.tablet} {
    gap: 12px;
  }

  ${media.portrait} {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    padding: 8px 0;
  }
`;

export const InProgressMissionStatusStyled = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px;
  gap: 12px;
  width: auto;
  height: 32px;
  flex: none;
  order: 2;
  flex-grow: 0;
  flex-shrink: 0;
  margin-right: auto;
`;

// Progress Bar Styles
export const ProgressBarStyled = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0px;
  gap: 0px;
  isolation: isolate;
  width: 100%;
  max-width: 480px;
  min-width: 160px;
  height: 16px;
  order: 2;
  flex: 1 1 auto;
  position: relative;

  ${media.tablet} {
    max-width: clamp(220px, 36vw, 480px);
  }

  ${media.portrait} {
    max-width: 100%;
    align-self: stretch;
  }
`;

export const StationMarkerStyled = styled.div`
  width: 16px;
  height: 16px;
  flex: none;
  position: relative;
  z-index: 2;

  &:last-child {
    position: absolute;
    right: 0;
    z-index: 0;
  }
`;

export const StopShapeStyled = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0px;
  isolation: isolate;
  position: absolute;
  height: 16px;
  width: 16px;
  left: 0;
  top: 0;

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const StationLabelStyled = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 0px;
  gap: 4px;
  position: absolute;
  width: auto;
  height: 16px;
  left: 1px;
  top: -20px;
`;

export const StationLabelTextStyled = styled.span`
  width: auto;
  height: 16px;
  font-family: "Readex Pro", sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 130%;
  text-align: center;
  color: #172a3f;
  flex: none;
  order: 0;
  flex-grow: 0;
`;

export const ProgressLineStyled = styled.div`
  /* Span between station markers (16px each at edges) instead of fixed 446px,
     so the line stays correct when the progress bar shrinks on tablet. */
  width: auto;
  height: 0px;
  border: 1px dashed #a4a4a4;
  flex: none;
  order: 2;
  flex-grow: 0;
  z-index: 1;
  position: absolute;
  left: 17px;
  right: 17px;
  top: calc(50% - 0.5px);
`;

export const VehicleProgressIndicatorStyled = styled.div<{ $progress: number }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0px;
  position: absolute;
  width: 40px;
  height: 40px;
  /* Position relative to the dashed line span (left:17px..right:17px).
     calc keeps the indicator correctly placed regardless of bar width
     (480px on desktop, ~36vw on tablet). */
  left: calc(17px + (100% - 34px) * ${(props) => props.$progress / 100} - 20px);
  top: calc(50% - 40px / 2);
  transform: rotate(-90deg);
  flex: none;
  order: 0;
  flex-grow: 0;
  z-index: 3;
`;

export const VehicleProgressIconStyled = styled.div`
  width: 40px;
  height: 40px;
  transform: rotate(-90deg);
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 1;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 36px;
    height: 36px;
    position: absolute;
    left: calc(50% - 36px / 2);
    top: calc(50% - 36px / 2);
  }
`;

// Cycles Counter Styles
export const CyclesContainerStyled = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px 0px 16px;
  gap: 16px;
  width: 39px;
  height: 68px;
  flex: none;
  order: 1;
  flex-grow: 0;
  flex-shrink: 0;
  align-self: stretch;
  margin-left: auto;
`;

export const CyclesFrameStyled = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding: 0px;
  width: 39px;
  height: 38px;
  flex: none;
  order: 0;
  flex-grow: 0;
`;

export const CyclesCounterStyled = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 0px;
  gap: 4px;
  width: 29px;
  height: 20px;
  flex: none;
  order: 0;
  flex-grow: 0;
  margin: -2px 0px;
`;

export const CyclesCurrentStyled = styled.span`
  width: auto;
  height: 20px;
  font-family: "Readex Pro", sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 128%;
  color: #d1d1d1;
  flex: none;
  order: 0;
  flex-grow: 0;
`;

export const CyclesSeparatorStyled = styled.div`
  width: 3px;
  height: 3px;
  background: #d1d1d1;
  flex: none;
  order: 1;
  flex-grow: 0;
`;

export const CyclesTotalStyled = styled.span`
  width: auto;
  height: 20px;
  font-family: "Readex Pro", sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 128%;
  color: #606060;
  flex: none;
  order: 2;
  flex-grow: 0;
`;

export const CyclesLabelStyled = styled.div`
  width: 39px;
  height: 20px;
  font-family: "Readex Pro", sans-serif;
  font-style: normal;
  font-weight: 300;
  font-size: 14px;
  line-height: 140%;
  color: #606060;
  flex: none;
  order: 1;
  flex-grow: 0;
`;

// Status Badge — shown in place of raw missionText.
// "ready"   → green tint (vehicle ready for work)
// "release" → amber tint (vehicle awaiting manual release before next mission)
// default   → neutral grey
export const StatusBadgeStyled = styled.span<{
  // DEV-3706: "offline" greys the badge for an offline vehicle.
  // DEV-4157: "warning" reds the badge for a sub-30% battery vehicle.
  $variant?: "ready" | "release" | "offline" | "warning";
}>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 20px;
  font-weight: 500;
  width: fit-content;

  ${media.portrait} {
    grid-area: badge;
    justify-self: start;
  }

  ${media.portraitMobile} {
    flex-shrink: 0;
    white-space: nowrap;
    font-size: 18px;
    padding: 4px 10px;
  }
  background-color: ${(props) =>
    props.$variant === "ready"
      ? "rgba(15, 98, 95, 0.15)"
      : props.$variant === "release"
      ? "rgba(184, 92, 0, 0.15)"
      : props.$variant === "offline"
      ? "rgba(96, 96, 96, 0.12)"
      : props.$variant === "warning"
      ? "rgba(198, 40, 40, 0.12)"
      : "#f0f0f0"};
  color: ${(props) =>
    props.$variant === "ready"
      ? "#0f625f"
      : props.$variant === "release"
      ? "#b85c00"
      : props.$variant === "offline"
      ? "#606060"
      : props.$variant === "warning"
      ? "#c62828"
      : "#606060"};
  border: 1px solid
    ${(props) =>
      props.$variant === "ready"
        ? "#0f625f"
        : props.$variant === "release"
        ? "#b85c00"
        : props.$variant === "offline"
        ? "#9a9a9a"
        : props.$variant === "warning"
        ? "#c62828"
        : "transparent"};

  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
  }
`;

export const StatusBadgeColumnStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  ${media.portrait} {
    display: contents;
  }

  /* Narrow portrait: regroup pill (+ mission name) as the right-most item
     of the compact top row — a content-height block kept vertically
     centered within the row. */
  ${media.portraitMobile} {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    order: 4;
    margin-left: auto;
    flex-shrink: 0;
    align-self: center;
  }
`;

export const MissionNameStyled = styled.span`
  font-family: "Readex Pro", sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #606060;
  padding-left: 4px;

  ${media.portrait} {
    grid-area: mission;
    align-self: center;
    padding-left: 0;
  }

  ${media.portraitMobile} {
    align-self: center;
    text-align: center;
    padding-left: 0;
  }
`;
